import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { Breadcrumb } from '@carouselgg/nexus/breadcrumb'
import { Button, ButtonLink } from '@carouselgg/nexus/button'
import { Callout } from '@carouselgg/nexus/callout'
import { ArrowRight, Settings } from '@carouselgg/nexus/icon'
import { useAuthStore } from '@carouselgg/auth/store'
import { getTrustStanding, type TrustStanding } from '../../api/compete'
import { ScopeRail, OVERALL_KEY } from './ScopeRail'
import { OverallView } from './OverallView'
import { GameView, type StandingState } from './GameView'
import { safeRedirect } from './trustCopy'
import styles from './TrustPage.module.css'

/**
 * /trust and /trust/:gameSlug — your Carousel score, each game's score, and how
 * to raise them. Arriving from a competition that asked for more
 * (`?required=60&redirect=/compete`) adds a banner with the way back.
 */
export function TrustPage() {
  const { gameSlug } = useParams()
  const [params] = useSearchParams()
  const user = useAuthStore((s) => s.user)
  const overall = useAuthStore((s) => s.trustScore)
  const games = useAuthStore((s) => s.gameTrustScores)
  const loadUser = useAuthStore((s) => s.loadUser)
  const [refreshing, setRefreshing] = useState(false)

  // Scores move whenever an account is linked or a match lands; read them fresh.
  useEffect(() => {
    void loadUser()
  }, [loadUser])

  const game = gameSlug ? games.find((g) => g.game_slug === gameSlug) : undefined
  // The game whose standing tells us which number competitions check — the one
  // on screen, or any, since per-game eligibility is one switch for all games.
  const standingGameId = gameSlug ? game?.game_id : games[0]?.game_id
  const [standing, setStanding] = useState<{ gameId: string; state: StandingState } | null>(null)

  useEffect(() => {
    if (!standingGameId) return
    let live = true
    getTrustStanding(standingGameId)
      .then((s: TrustStanding) => { if (live) setStanding({ gameId: standingGameId, state: { status: 'ready', standing: s } }) })
      .catch((err: unknown) => {
        if (live) setStanding({ gameId: standingGameId, state: { status: 'error', message: err instanceof Error ? err.message : 'Could not load your standing for this game' } })
      })
    return () => { live = false }
  }, [standingGameId])

  const standingState: StandingState = standing && standing.gameId === standingGameId ? standing.state : { status: 'loading' }
  const scope = standingState.status === 'ready' ? standingState.standing.scope : null

  const required = Number(params.get('required')) || 0
  const redirect = safeRedirect(params.get('redirect'))

  const name = user?.gamer_tag || user?.username || 'You'
  const crumbs = [
    { label: name, href: user ? `/u/${user.username}` : undefined },
    gameSlug ? { label: 'Trust score', href: '/trust' } : { label: 'Trust score' },
    ...(gameSlug ? [{ label: game?.game_name ?? 'Game' }] : []),
  ]

  const retry = async () => {
    setRefreshing(true)
    try { await loadUser() } finally { setRefreshing(false) }
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={crumbs} renderLink={(crumb, children) => <Link to={crumb.href!}>{children}</Link>} />

      {!overall ? (
        <Callout variant="error" icon>
          <div className={styles.calloutBody}>
            <span>We couldn't load your trust score. Check your connection and try again.</span>
            <Button variant="secondary" size="sm" onClick={() => void retry()} loading={refreshing}>Try again</Button>
          </div>
        </Callout>
      ) : (
        <>
          {required > 0 && (
            <Callout variant={overall.score >= required ? 'success' : 'info'} icon className={styles.required}>
              <div className={styles.calloutBody}>
                <span>The competition you're joining asks for {required}. You're at {overall.score}.</span>
                {overall.score >= required && redirect && (
                  <ButtonLink to={redirect} size="sm">Continue <ArrowRight size={14} aria-hidden="true" /></ButtonLink>
                )}
              </div>
            </Callout>
          )}

          <ScopeRail overall={overall} games={games} selected={gameSlug ?? OVERALL_KEY} />

          {!gameSlug && <OverallView overall={overall} games={games} scope={scope} />}
          {gameSlug && game && <GameView game={game} overall={overall} standing={standingState} />}
          {gameSlug && !game && (
            <div className={styles.notFound}>
              <h1 className={styles.notFoundTitle}>No score for that game</h1>
              <p className={styles.quiet}>
                You don't have a score for “{gameSlug}”. Link an account for a game and it gets its own score.
              </p>
              <ButtonLink to="/trust" variant="secondary" size="sm">Back to your trust score</ButtonLink>
            </div>
          )}

          <div className={styles.connections}>
            <span className={styles.connectionsText}>
              <b>Accounts, phone and passkeys all live in Connections</b>
              <span className={styles.quiet}>Link or remove anything that feeds your scores in one place.</span>
            </span>
            <ButtonLink to="/settings/connections" variant="secondary" size="sm">
              <Settings size={14} aria-hidden="true" /> Open Connections
            </ButtonLink>
          </div>
        </>
      )}
    </div>
  )
}
